import { grey } from '@material-ui/core/colors';
import List from '@material-ui/core/List';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import React, { useEffect, useState } from 'react';
import * as showdown from 'showdown';
import LoadingButton from '../../../common/LoadingButton';
import ApprovalTimelineEvent from './ApprovalTimelineEvent';
import BasicTimelineEvent from './BasicTimelineEvent';
import CommentTimelineEvent from './CommentTimelineEvent';
import ActionsListPopover from './ActionsListPopover';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        timeline: {
            position: 'relative',
            marginBottom: 24,
            '&::before': {
                content: "''",
                borderLeft: `2px solid ${theme.palette.type === 'light' ? grey[300] : grey[800]}`,
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 36,
            }
        },
        commentTextField: {
            marginBottom: 24
        },
        commentInputPanel: {
            border: `1px solid ${theme.palette.divider}`,
            padding: 16
        },
        timelineEvent: {
            border: `1px solid ${theme.palette.type === 'light' ? grey[300] : grey[800]}`,
            marginBottom: 16,
            backgroundColor: theme.palette.background.default
        }
    })
);

function getNameFromEmail(email: string) {
    return email.split('@')[0].split('.').join(" ").replace(/(^|\s)[a-z]/g, (f) => f.toUpperCase());
}

interface Props {
    approvalRecord: any;
    onAddComment: (comment: string) => Promise<any>;
}

const showdownConverter = new showdown.Converter({
    strikethrough: true,
    simpleLineBreaks: true,
    tasklists: true,
    openLinksInNewWindow: true,
    emoji: true,
    literalMidWordUnderscores: true,
    simplifiedAutoLink: true,
});

showdownConverter.setFlavor('github');

function markdownToHtml(markdown) {
    return { __html: showdownConverter.makeHtml(markdown) }
};

function ActivityPanel(props: Props) {
    const { approvalRecord } = props;
    const classes = useStyles({});
    const [commentToAdd, setCommentToAdd] = useState('');
    const [addingComment, setAddingComment] = useState(false);
    const [events, setEvents] = useState([]);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedPopoverEvent, setSelectedPopoverEvent] = useState(null);

    useEffect(() => {
        const approvalEventsMap = approvalRecord.Approvals.reduce((accumulator, record) => {
            const key = `${record.User}:${record.ApprovalTime}`;
            if (!accumulator[key]) {
                accumulator[key] = {
                    id: key,
                    type: 'approval',
                    email: record.User,
                    timestamp: record.ApprovalTime,
                    actions: new Set(),
                    approved: record.ApprovalValue
                };
            }

            // Add Action
            accumulator[key].actions.add(record.ActionName.startsWith("PreApprovalGroup:") ? 'Custom Approval' : record.ActionName);

            return accumulator;
        }, {});

        const eventList = [
            {
                id: 'creation-event',
                type: 'basic',
                title: `${getNameFromEmail(approvalRecord.Requester)} submitted approval request`,
                email: approvalRecord.Requester,
                timestamp: approvalRecord.ApprovalRequestCreationTime
            },
            ...Object.keys(approvalEventsMap).map(key => ({ ...approvalEventsMap[key], actions: Array.from(approvalEventsMap[key].actions) })),
            ...approvalRecord.Comments.map(comment => ({
                id: `${comment.EventTime}:${comment.User}`,
                type: 'comment',
                email: comment.User,
                timestamp: comment.EventTime,
                content: markdownToHtml(comment.Comment)
            }))
        ];

        eventList.sort((a, b) => {
            return a.timestamp - b.timestamp
        })

        // Add standard event fields
        eventList.forEach(event => {
            event.user = getNameFromEmail(event.email);
            event.timestamp = event.timestamp * 1000;
        });

        setEvents(eventList);

    }, [approvalRecord]);

    function onCommentInputChanged(event: React.ChangeEvent<HTMLInputElement>) {
        setCommentToAdd(event.target.value);
    }

    function onAddComment() {
        setAddingComment(true);
        props.onAddComment(commentToAdd).then(() => {
            setCommentToAdd('')
            setAddingComment(false);
        });
    }

    function onOpenPopover(target: HTMLElement, event: any){
        setAnchorEl(target);
        setSelectedPopoverEvent(event);
    }

    function onClosePopover() {
        setAnchorEl(null);
        setSelectedPopoverEvent(null);
    }

    return (
        <div>
            <List disablePadding className={classes.timeline}>
                {events.map(event => {
                    switch (event.type) {
                        case 'approval':
                            return <ApprovalTimelineEvent
                                key={event.id}
                                event={event}
                                className={classes.timelineEvent}
                                onOpenPopover={onOpenPopover}
                                onClosePopover={onClosePopover}
                            />;
                        case 'comment':
                            return <CommentTimelineEvent
                                key={event.id}
                                event={event}
                                className={classes.timelineEvent}
                            />;
                        case 'basic':
                            return <BasicTimelineEvent
                                key={event.id}
                                event={event}
                                className={classes.timelineEvent}
                            />;
                        default:
                            return <div>Timeline Event Type {event.type} Not Supported</div>;
                    }
                })}
            </List>
            <div className={classes.commentInputPanel}>
                <TextField
                    multiline
                    value={commentToAdd}
                    rows="5"
                    placeholder="Leave a comment"
                    className={classes.commentTextField}
                    margin="none"
                    fullWidth
                    onChange={onCommentInputChanged}
                />
                <LoadingButton
                    loading={addingComment}
                    variant="contained"
                    color="primary"
                    disabled={!commentToAdd || addingComment}
                    onClick={onAddComment}>
                    Add Comment
                </LoadingButton>
            </div>
            <ActionsListPopover event={selectedPopoverEvent} anchorEl={anchorEl} onPopoverClose={onClosePopover} />
        </div>
    );
};

export default ActivityPanel;
